import { Bitmap, IObject, ITween, dispatcher, ingenuity, IEvent, Container, delayedCall, killDelayedCall } from "@quest/base-core";
import { BitmapImage, Emitter, Linear } from "@quest/pixi-bridge";

import { eventDispatchers } from "../Constants/EventDispatchers";
import { BonusSymTrailView } from "./BonusSymTrailView";
import { GameParserModel } from "../Platform/GameParserModel";
import { gameConstants } from "../Constants/gameConstants";
import { SoundConstantGame } from "../Constants/SoundConstantGame";
import { SlotConstantsBehavior } from "@quest/murka-games-behavior-panther";

export class BonusSymTrailController {
  private view: BonusSymTrailView;
  private trailCounter: number = 0;
  private tweenArray: ITween[] = [];
  private gridData: number[][];
  private reelNumber: number = 0;
  private col: number = 0;
  private bonusTrailComplete: boolean = false;
  private particleData: any = {
    emitterType: 1,
    id: "coinEmitter_1",
    autoStart: false,
    animConfig: [
      {
        framerate: 20,
        loop: true,
        image: "Blue_pixel",
        generateFrames: false,
        frame: 0,
      },
    ],
  };

  private particleDataGreen: any = {
    emitterType: 1,
    id: "coinEmitter_2",
    autoStart: false,
    animConfig: [
      {
        framerate: 20,
        loop: true,
        image: "Green_pixel",
        generateFrames: false,
        frame: 0,
      },
    ],
  };

  private particleDataRed: any = {
    emitterType: 1,
    id: "coinEmitter_3",
    autoStart: false,
    animConfig: [
      {
        framerate: 20,
        loop: true,
        image: "red_pixel",
        generateFrames: false,
        frame: 0,
      },
    ],
  };

  private particleConfig: any = {
    alpha: {
      start: 1,
      end: 0,
    },
    scale: {
      start: 0.1,
      end: 1.4,
      minimumScaleMultiplier: 3,
    },
    color: {
      start: "#bd563d",
      end: "#eb1221",
    },
    speed: {
      start: 50,
      end: 50,
      minimumSpeedMultiplier: 1,
    },
    acceleration: {
      x: 0,
      y: 0,
    },
    maxSpeed: 0,
    startRotation: {
      min: 0,
      max: 360,
    },
    noRotation: false,
    rotationSpeed: {
      min: 0,
      max: 0,
    },
    lifetime: {
      min: 0.2,
      max: 0.8,
    },
    blendMode: "add",
    frequency: 0.001,
    emitterLifetime: -1,
    maxParticles: 500,
    pos: {
      x: 0,
      y: 0,
    },
    addAtBack: false,
    spawnType: "point",
  };

  constructor(view: BonusSymTrailView) {
    this.view = view;
    this.subscribeEvents();
  }

  /**
   * subscribe all events
   */
  private subscribeEvents(): void {
    this.unSubscribeEvents();
    dispatcher.on(eventDispatchers.START_BONUS_SYM_TRAIL, this.startBonusSymTrail, this);
    dispatcher.on(eventDispatchers.RESET_BONUS_TRAIL_DATA, this.resetBonusTrailData, this);
  }

  /**
   * un subscribe all events
   */
  private unSubscribeEvents(): void {
    dispatcher.off(eventDispatchers.START_BONUS_SYM_TRAIL, this.startBonusSymTrail, this);
    dispatcher.off(eventDispatchers.RESET_BONUS_TRAIL_DATA, this.resetBonusTrailData, this);
  }

  // Add to reset bonus trail data
  private resetBonusTrailData(): void {
    this.bonusTrailComplete = false;
    this.reelNumber = 0;
    this.col = 0;
    this.tweenArray = [];
    this.trailCounter = 0;
    const trailAnimationContainer: Container = this.view.getContainerByID("trailAnimationContainer");
    trailAnimationContainer.children.forEach((child: Container) => {
      child.children = [];
    });
  }

  // Add to start bonus symbol trail
  private startBonusSymTrail(evt: IEvent): void {
    this.gridData = (ingenuity.parserModel as GameParserModel).getReelGrid();
    this.checkForBonusSymbols();
  }

  // Add to find bonus symbols offset
  private checkForBonusSymbols(): void {
    if (this.col < this.gridData[this.reelNumber].length) {
      if (ingenuity.configData.bonusSymbols.includes(this.gridData[this.reelNumber][this.col])) {
        let character = this.gridData[this.reelNumber][this.col];
        let endPosition: { x: number; y: number } = this.getEndPosition(this.gridData[this.reelNumber][this.col]);
        let x = this.view.json.smokeSpriteXY[this.reelNumber][this.col][0];
        let y = this.view.json.smokeSpriteXY[this.reelNumber][this.col][1];
        this.playThemeSound();
        this.playSkullBlust(x, y, this.reelNumber, character);
        this.playTrailAnimation(this.reelNumber, this.col, endPosition, character);
      } else {
        this.col++;
        this.checkForBonusSymbols();
      }
    } else if (this.reelNumber < this.gridData.length - 1) {
      this.reelNumber++;
      this.col = 0;
      this.checkForBonusSymbols();
    }
    if (this.reelNumber == this.gridData.length - 1 && this.col == this.gridData[this.reelNumber].length && !this.bonusTrailComplete) {
      this.bonusTrailComplete = true;
      dispatcher.fireEvent(eventDispatchers.BONUS_TRAIL_COMPLETED);
    }
  }
  /**
   * play trail sound as per skull symbol 
   */
  private playThemeSound(): void {
    (ingenuity.soundManager as any).stop(SoundConstantGame.TRAIL_ADD_SOUND, 1, false, SlotConstantsBehavior.SOUND_LIST_PRELOAD);
    let character = this.gridData[this.reelNumber][this.col];
    switch (character) {
      case gameConstants.CHARACTER_MULTIPLIER:
        (ingenuity.soundManager as any).playSound(SoundConstantGame.RED_TRAIL_SOUND, 1, false, SlotConstantsBehavior.SOUND_LIST_PRELOAD);
        break;
      case gameConstants.CHARACTER_BIG_PRIZE:
        (ingenuity.soundManager as any).playSound(SoundConstantGame.BLUE_TRAIL_SOUND, 1, false, SlotConstantsBehavior.SOUND_LIST_PRELOAD);
        break;
      case gameConstants.CHARACTER_COLLECT_ALL:
        (ingenuity.soundManager as any).playSound(SoundConstantGame.GREEN_TRAIL_SOUND, 1, false, SlotConstantsBehavior.SOUND_LIST_PRELOAD);
        break;
    }
  }

  /**
   * function to set color of trail as per skull symbol
   * @param col 
   * @param row 
   * @returns 
   */
  private getThemeColor(col: number, row: number): Object {
    let data = {
      themeColor: {
        start: "#bd563d",
        end: "#eb1221",
      },
      themeParticleData: this.particleData,
    };
    let character = this.gridData[this.reelNumber][this.col];
    switch (character) {
      case gameConstants.CHARACTER_MULTIPLIER:
        data = {
          themeColor: {
            start: "#bd563d",
            end: "#eb1221",
          },
          themeParticleData: this.particleDataRed,
        };
        this.particleDataRed.id = this.view.json.smokeSprite.id + col + row;
        break;
      case gameConstants.CHARACTER_BIG_PRIZE:
        data = {
          themeColor: {
            start: "#bd563d",
            end: "#eb1221",
          },
          themeParticleData: this.particleData,
        };
        this.particleData.id = this.view.json.smokeSprite.id + col + row;

        break;
      case gameConstants.CHARACTER_COLLECT_ALL:
        data = {
          themeColor: {
            start: "#bd563d",
            end: "#eb1221",
          },
          themeParticleData: this.particleDataGreen,
        };
        this.particleDataGreen.id = this.view.json.smokeSprite.id + col + row;

        break;
    }
    return data;
  }

  /**
   * function to play blast animation
   * @param x 
   * @param y 
   * @param col 
   * @param character 
   */
  private playSkullBlust(x: number, y: number, col: number, character: number) {
    let spine = this.view.getSpineById("skull_effects").json;
    let child = this.view.createCloneSpine(spine);
    child.x = x;
    child.visible = true;
    child.y = y;
    this.view.getContainerByID("trailAnimationContainer_" + col).addChild(child);
    switch (character) {
      case gameConstants.CHARACTER_MULTIPLIER:
        child.playAnim("Red_skull_effect");
        (ingenuity.parserModel as GameParserModel).setRedElementCount((ingenuity.parserModel as GameParserModel).getRedElementCount() + 1);
        break;
      case gameConstants.CHARACTER_BIG_PRIZE:
        child.playAnim("Blue_skull_effect");
        (ingenuity.parserModel as GameParserModel).setBlueElementCount(
          (ingenuity.parserModel as GameParserModel).getBlueElementCount() + 1
        );
        break;
      case gameConstants.CHARACTER_COLLECT_ALL:
        child.playAnim("Green_skull_effect");
        (ingenuity.parserModel as GameParserModel).setGreenElementCount(
          (ingenuity.parserModel as GameParserModel).getGreenElementCount() + 1
        );
        break;
    }
  }

  /**
   * get end position for trail according to skull symbol
   * @param symbolId
   */
  private getEndPosition(symbolId: number): { x: number; y: number } {
    let endPos: { x: number; y: number } = { x: 0, y: 0 };
    if (symbolId == gameConstants.CHARACTER_MULTIPLIER) {
      //red
      endPos = { x: 195, y: 570 };
    } else if (symbolId == gameConstants.CHARACTER_BIG_PRIZE) {
      //blue
      endPos = { x: 195, y: 430 };
    } else if (symbolId == gameConstants.CHARACTER_COLLECT_ALL) {
      //green
      endPos = { x: 195, y: 300 };
    }
    return endPos;
  }

  /**
   * This method is use to play bonus trail animation from reel to pot
   */
  private playTrailAnimation(col: number, row: number, endPosition: { x: number; y: number }, character: number): void {
    //Use to create trail image
    let smokeSprite: any = this.createTrailImage(col, row, character);
    //Use to create trail emitter
    this.createTrailEmitter(smokeSprite, col, row);
    //Use to play trail animation on each bonus landed
    this.tostartTrail(col, row, smokeSprite, endPosition, character);
    this.trailCounter += 1;
  }

  /**
   * This method is use to create trail image
   */
  private createTrailImage(colNum: number, rowNum: number, character: number): BitmapImage {
    let smokeSprite: BitmapImage = new BitmapImage(ingenuity.currentGame, 0, 0, this.view.json.smokeSprite.id);
    smokeSprite.x = this.view.json.smokeSpriteXY[colNum][rowNum][0];
    smokeSprite.y = this.view.json.smokeSpriteXY[colNum][rowNum][1];

    smokeSprite.name = this.view.json.smokeSprite.name + colNum + rowNum;
    smokeSprite.alpha = this.view.json.smokeSprite.alpha;
    smokeSprite.scale.set(this.view.json.smokeSprite.scale);
    smokeSprite.anchor.set(this.view.json.smokeSprite.anchorX, this.view.json.smokeSprite.anchorY);
    let smoke: Bitmap = this.view.getImageById("blueParticle");
    smoke.parent.addChild(smokeSprite);
    return smokeSprite;
  }

  /**
   * This method create emitter for trail
   */
  private createTrailEmitter(smokeSprite: BitmapImage, col: number, row: number): void {
    let data: any = this.getThemeColor(col, row);
    let particleDataTheme = data.themeParticleData;
    this.particleConfig.color = data.themeColor;
    const particle: Emitter = ingenuity.currentGame.add.emitter(
      this.view.getContainerByID("trailAnimationContainer_" + col),
      particleDataTheme,
      this.particleConfig
    ) as Emitter;
    particle.autoUpdate = true;
    particle.emit = true;
    particle.updateOwnerPos(smokeSprite.x, smokeSprite.y);
  }

  /**
   * This method is use to start trail
   */
  private tostartTrail(
    colNum: number,
    rowNum: number,
    smokeSprite: BitmapImage,
    endPosition: { x: number; y: number },
    character: number
  ): void {
    let counter: number = this.trailCounter;
    let curveVal = -0.7;
    let startPosX = this.view.json.smokeSpriteXY[colNum][rowNum][0];
    let startPosY = this.view.json.smokeSpriteXY[colNum][rowNum][1];
    let endPosX = endPosition.x;
    let endPosY = endPosition.y;
    let position = this.bezierCurve(startPosX, startPosY, endPosX, endPosY, curveVal);
    let trailTime = this.getTrailTime(character);
    this.tweenArray.push(
      ingenuity.currentGame.add.tween(smokeSprite).to({ x: position[0], y: position[1] }, trailTime[colNum][rowNum], undefined, true)
    );
    this.tweenArray[counter].onComplete.addOnce(() => {
      this.col++;
      dispatcher.fireEvent(eventDispatchers.PLAY_HIT_ANIMATION, character);
      (ingenuity.soundManager as any).playSound(SoundConstantGame.TRAIL_ADD_SOUND, 1, false, SlotConstantsBehavior.SOUND_LIST_PRELOAD);
      ingenuity.currentGame.particles.getEmitterById(this.tweenArray[counter].target.name).emit = false;
      let trailAlphaTween: ITween = ingenuity.currentGame.add
        .tween(this.view.getContainerByID("trailAnimationContainer_" + colNum))
        .to({ alpha: 0 }, 300, Linear.None, true, 0, 0, false);
      trailAlphaTween.onComplete.addOnce(() => {
        delayedCall(
          "nextTrailDelay",
          500,
          () => {
            killDelayedCall("nextTrailDelay");
            this.checkForBonusSymbols();
          },
          this
        );
        let em: Emitter;
        em = ingenuity.currentGame.particles.getEmitterById(this.tweenArray[counter].target.name);
        em && (em.autoUpdate = false);
        em && em.destroy();
        this.view.getContainerByID("trailAnimationContainer_" + colNum).alpha = 1;
        this.tweenArray[counter].stop();
        this.tweenArray[counter].onUpdateCallback = null;
        ingenuity.currentGame.tweens.remove(this.tweenArray[counter]);
        this.tweenArray[counter].target.parent.removeChild(this.tweenArray[counter].target);
      }, this);
    }, this);

    this.tweenArray[counter].onUpdateCallback((evt: IObject) => {
      let em: Emitter;
      em = ingenuity.currentGame.particles.getEmitterById(evt.target.name);
      em && em.updateOwnerPos(evt.target.x, evt.target.y);
    });
  }
/**
 * to get total travel time
 * @param symbolId 
 * @returns trail time from source to destination
 */
  private getTrailTime(symbolId: number): any {
    let trailTime;
    if (symbolId == gameConstants.CHARACTER_MULTIPLIER) {
      //red
      trailTime = this.view.json.redTrailTimers;
    } else if (symbolId == gameConstants.CHARACTER_BIG_PRIZE) {
      //blue
      trailTime = this.view.json.blueTrailTimers;
    } else if (symbolId == gameConstants.CHARACTER_COLLECT_ALL) {
      //green
      trailTime = this.view.json.greenTrailTimers;
    }
    return trailTime;
  }

  //create a curve path array position
  private bezierCurve(startX: number, startY: number, endX: number, endY: number, curveFactor: number): number[][] {
    // startX: X-coordinate of the starting point of the line
    // startY: Y-coordinate of the starting point of the line
    // endX: X-coordinate of the ending point of the line
    // endY: Y-coordinate of the ending point of the line
    // curveFactor: Factor determining the amount of curvature in the curve

    // Calculate control point coordinates
    const controlX = (startX + endX) / 2 - Math.abs(startX - endX) * curveFactor; // X-coordinate of the control point (midpoint)
    const controlY = (startY + endY) / 2; // Y-coordinate of the control point

    // Arrays to store X and Y coordinates of points along the curve
    let curveX: number[] = []; // Array to store X coordinates
    let curveY: number[] = []; // Array to store Y coordinates

    // Number of points to calculate along the curve
    const numberOfPoints = 50; // You can adjust this value based on the desired smoothness of the curve

    // Loop to calculate coordinates of points along the curve
    for (let i = 0; i <= numberOfPoints; i++) {
      const t = i / numberOfPoints; // Parameterization along the Bézier curve (ranges from 0 to 1)
      const x = Math.pow(1 - t, 2) * startX + 2 * (1 - t) * t * controlX + Math.pow(t, 2) * endX; // X-coordinate of the point on the curve
      const y = Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY; // Y-coordinate of the point on the curve
      curveX.push(x); // Add X-coordinate to the array
      curveY.push(y); // Add Y-coordinate to the array
    }

    // Return arrays of X and Y coordinates of points along the curve
    return [curveX, curveY];
  }
}
